// Travel modes and the deterministic travel numbers of the feasibility rules (CHT-744, contract
// `docs/architecture/milestone-d-contracts.md` 4.3 and 5.1). Vendor-free: no provider name, type or URL appears here. The domain owns
// the vocabulary; the application port and the adapter map to it.
/** The modes a route edge is checked in. A flight, a ferry or a car service is a TRANSIT item that someone entered, not a mode. */
export const travelModes = Object.freeze(["WALK", "DRIVE", "TRANSIT"]);
export const isTravelMode = (value) => typeof value === "string" && travelModes.includes(value);
/** The mode an edge is checked in unless the caller says otherwise. DRIVE is the fastest of the three, so a verdict of "infeasible" under it holds for the others too. */
export const DEFAULT_TRAVEL_MODE = "DRIVE";
const EARTH_RADIUS_METERS = 6_371_008.8;
const toRadians = (degrees) => (degrees * Math.PI) / 180;
/** The great-circle distance between two points, in metres. */
export function haversineMeters(a, b) {
    const dLat = toRadians(b.latitude - a.latitude);
    const dLon = toRadians(b.longitude - a.longitude);
    const h = Math.sin(dLat / 2) ** 2 +
        Math.cos(toRadians(a.latitude)) * Math.cos(toRadians(b.latitude)) * Math.sin(dLon / 2) ** 2;
    return 2 * EARTH_RADIUS_METERS * Math.asin(Math.min(1, Math.sqrt(h)));
}
/**
 * The static heuristic (used only when `LAFAYETTE_ROUTES_STATIC_FALLBACK=on` and the provider is down). It is deliberately slow and
 * roundabout: a road is longer than the straight line (`circuity`) and the speeds are low. It is never provider data, and an
 * estimate that comes from it is never `VERIFIED`: the edge stays `UNVERIFIED` and the customer text says so.
 */
export const STATIC_TRAVEL_HEURISTIC = Object.freeze({
    circuity: 1.4,
    speedKmh: Object.freeze({ WALK: 4, DRIVE: 30, TRANSIT: 20 }),
});
export function staticTravelEstimate(from, to, mode) {
    const distanceMeters = Math.round(haversineMeters(from, to) * STATIC_TRAVEL_HEURISTIC.circuity);
    const metersPerSecond = (STATIC_TRAVEL_HEURISTIC.speedKmh[mode] * 1000) / 3600;
    return { distanceMeters, durationSeconds: Math.ceil(distanceMeters / metersPerSecond) };
}
